'use client';

import { ArrowLeft01Icon, Download01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import Link from 'next/link';
import { useState } from 'react';
import { ArkyvWordmark } from '@/components/common/arkyv-wordmark';
import { useLocale } from '@/components/common/locale-provider';
import { toast } from '@/components/shared/toast';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { GridPattern } from '@/components/ui/grid-pattern';
import { usePluginUpdateManifest } from '@/hooks/use-plugin-update-manifest';
import { useTranslations } from '@/hooks/use-translations';
import { PLUGIN_DOWNLOADS, type PluginDownload } from '@/lib/downloads/config';
import { cn } from '@/lib/shadcn-utils';
import { usePluginDownloadAction } from './use-plugin-download-action';

export function DownloadsPageContent() {
  const { locale } = useLocale();
  const { t } = useTranslations(locale);
  const manifest = usePluginUpdateManifest();
  const [copyFailedDownloadIds, setCopyFailedDownloadIds] = useState<
    Set<PluginDownload['id']>
  >(() => new Set());
  const { canOpenExternal, isRhinoHost, startDownload } =
    usePluginDownloadAction();

  // Overlay served release metadata (latest version, URL) on the static
  // download config; fall back to build-time values while it loads.
  const downloads = PLUGIN_DOWNLOADS.map((download): PluginDownload => {
    const entry = manifest?.entries.find(
      (candidate) => candidate.id === download.id,
    );
    if (!entry) {
      return download;
    }

    return {
      ...download,
      url: entry.downloadUrl,
      filename: entry.filename,
      version: entry.latestVersion,
    };
  });

  const handleDownload = async (download: PluginDownload) => {
    const outcome = await startDownload(download);
    if (outcome === 'link_copied') {
      setCopyFailedDownloadIds((current) => {
        const next = new Set(current);
        next.delete(download.id);
        return next;
      });
      toast.success(t('downloads.rhinoEmbedded.linkCopied'));
    } else if (outcome === 'copy_failed') {
      setCopyFailedDownloadIds((current) => new Set(current).add(download.id));
      toast.error(t('downloads.rhinoEmbedded.copyFailed'));
    }
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-muted">
      <div className="absolute top-4 left-4 z-10">
        <Link
          href="/"
          aria-label={t('common.back')}
          className={buttonVariants({ variant: 'outline', size: 'icon-lg' })}
        >
          <HugeiconsIcon icon={ArrowLeft01Icon} strokeWidth={2} />
        </Link>
      </div>
      <GridPattern
        width={30}
        height={30}
        strokeDasharray="4 4"
        className={cn(
          'mask-[radial-gradient(700px_circle_at_center,white,transparent)]',
          'fill-muted-foreground/20 stroke-muted-foreground/20 dark:fill-muted-foreground/20 dark:stroke-muted-foreground/20',
        )}
      />
      <div className="relative flex min-h-screen items-center justify-center px-4 py-12">
        <div className="flex w-full max-w-3xl flex-col items-center gap-y-8">
          <div className="flex flex-col items-center gap-y-4 text-center">
            <ArkyvWordmark className="text-4xl" />
            <div className="space-y-2">
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                {t('downloads.pageTitle')}
              </h1>
              <p className="max-w-xl text-muted-foreground">
                {t('downloads.pageDescription')}
              </p>
            </div>
          </div>

          <div className="grid w-full gap-4 md:grid-cols-2">
            {downloads.map((download) => (
              <Card className="w-full" key={download.id}>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {t(download.titleKey)}
                  </CardTitle>
                  <CardDescription>
                    {t(download.descriptionKey, {
                      versions: download.versions.join(', '),
                    })}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {!download.url && download.unavailableKey ? (
                    <p className="text-sm text-muted-foreground">
                      {t(download.unavailableKey)}
                    </p>
                  ) : null}
                  {isRhinoHost && !canOpenExternal && download.url ? (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        {t('downloads.rhinoEmbedded.instructions')}
                      </p>
                      {copyFailedDownloadIds.has(download.id) ? (
                        <input
                          aria-label={t('downloads.rhinoEmbedded.linkLabel')}
                          className="w-full rounded-md border bg-background px-3 py-2 text-xs text-muted-foreground"
                          onFocus={(event) => event.currentTarget.select()}
                          readOnly
                          value={download.url}
                        />
                      ) : null}
                    </div>
                  ) : null}
                  <Button
                    type="button"
                    onClick={() => handleDownload(download)}
                    className="w-full"
                    disabled={!download.url}
                  >
                    <HugeiconsIcon icon={Download01Icon} strokeWidth={2} />
                    {isRhinoHost
                      ? t(
                          canOpenExternal
                            ? 'downloads.rhinoEmbedded.openInBrowser'
                            : 'downloads.rhinoEmbedded.copyLink',
                        )
                      : t('downloads.downloadButton')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
